import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import QuestionResponseView from '../../components/marketing/QuestionResponseView';
import ResponseOptionSelector from '../../components/marketing/ResponseOptionSelector';
import EventActionView from '../../components/marketing/EventActionView';
import SignupRequestView from '../../components/marketing/SignupRequestView';
import ForwardCaptureForm from '../../components/marketing/ForwardCaptureForm';
import EventRSVPIntentView from '../../components/marketing/EventRSVPIntentView';
import EventCheckinIntentView from '../../components/marketing/EventCheckinIntentView';
import CalendarBookingIntentView from '../../components/marketing/CalendarBookingIntentView';
import Logo from '../../components/common/Logo';
import { getCachedPreLaunchEnabled } from '../../utils/preLaunchConfigCache';
import useConsentPreference from '../../hooks/useConsentPreference';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

function QuestionResponseHandler({ config, onSelect }) {
  return (
    <ResponseOptionSelector
      options={config.options}
      questionText={config.questionText}
      responseType={config.responseType}
      onSelect={onSelect}
    />
  );
}

function EventRSVPHandler({ config: _config, intentCode, cid }) {
  return <EventRSVPIntentView intentCode={intentCode} hubspotContactId={cid || null} />;
}

function EventCheckinHandler({ config: _config, intentCode }) {
  return <EventCheckinIntentView intentCode={intentCode} />;
}

function CalendarBookingHandler({ config: _config, intentCode, cid }) {
  return <CalendarBookingIntentView intentCode={intentCode} refId={cid || null} />;
}

function SignupRequestHandler({ config, onSelect }) {
  return (
    <SignupRequestView
      questionText={config.questionText}
      options={config.options}
      utm={config.utm}
      destinationUrl={config.destinationUrl}
      onSelect={onSelect}
    />
  );
}

function EventActionHandler({ config, onSelect }) {
  return (
    <EventActionView
      event={config.event}
      options={config.options}
      questionText={config.questionText}
      intentType={config.intentType}
      onSelect={onSelect}
    />
  );
}

function UnknownIntentError({ intentType: _intentType }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <h1 className="text-xl font-semibold text-gray-900 mb-3">Page unavailable</h1>
      <p className="text-gray-600">This content type is not yet supported. Please check back later.</p>
    </div>
  );
}

export const INTENT_HANDLERS = {
  question_response: QuestionResponseHandler,
  event_rsvp: EventRSVPHandler,
  event_checkin: EventCheckinHandler,
  calendar_booking: CalendarBookingHandler,
  signup_request: SignupRequestHandler,
  rsvp: EventActionHandler,
  checkin: EventActionHandler,
  walk_in: EventActionHandler,
};

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export default function IntentResponsePage() {
  const { intentCode } = useParams();
  const [searchParams] = useSearchParams();
  const responseValue = searchParams.get('v');
  const contactId = searchParams.get('ref');
  const { consent } = useConsentPreference();

  const [state, setState] = useState('loading');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [configData, setConfigData] = useState(null);

  const processResponse = useCallback(async function processResponseInner(value, formData) {
    setState('loading');
    try {
      const hubspotUtk = consent?.marketing ? getCookie('hubspotutk') : null;
      const res = await fetch(`${API_BASE_URL}/api/v1/marketing/intent-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intentCode,
          responseValue: value,
          contactId: contactId || undefined,
          hubspotUtk: hubspotUtk || undefined,
          ...(formData && formData.email && { email: formData.email }),
          ...(formData && formData.firstName && { firstName: formData.firstName }),
          ...(formData && formData.lastName && { lastName: formData.lastName }),
          ...(formData && formData.phone && { phone: formData.phone }),
          ...(formData && formData.company && { company: formData.company }),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setState('error');
        setError({ status: res.status, code: data.error });
        return;
      }

      const data = await res.json();
      setResult(data);
      setState('done');
    } catch {
      setState('error');
      setError({ status: 500, code: 'NETWORK_ERROR' });
    }
  }, [intentCode, contactId, consent]);

  const fetchConfig = useCallback(async function fetchConfigInner() {
    setState('loading');
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/marketing/intent-config/${intentCode}`);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setState('error');
        setError({ status: res.status, code: data.error });
        return;
      }

      const data = await res.json();
      setConfigData(data);

      if (!contactId && data.forwardCaptureHeading) {
        setState('capture');
      } else {
        setState('config');
      }
    } catch {
      setState('error');
      setError({ status: 500, code: 'NETWORK_ERROR' });
    }
  }, [intentCode, contactId]);

  useEffect(() => {
    if (responseValue) {
      processResponse(responseValue);
    } else {
      fetchConfig();
    }
  }, [responseValue, processResponse, fetchConfig]);

  const handleCaptureComplete = useCallback(() => {
    setState('config');
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {!getCachedPreLaunchEnabled() && (
          <div className="flex justify-center mb-8">
            <Logo variant="detailed" />
          </div>
        )}

        {state === 'loading' && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" role="status" aria-label="Loading" />
          </div>
        )}

        {state === 'done' && result && (
          result.identificationCase === 'forward_detected' ? (
            <ForwardCaptureForm
              intentCode={intentCode}
              forwardCapture={result.forwardCapture}
            />
          ) : (
            <QuestionResponseView displayContent={result.displayContent} />
          )
        )}

        {state === 'capture' && configData && (
          <ForwardCaptureForm
            intentCode={intentCode}
            forwardCapture={{
              heading: configData.forwardCaptureHeading,
              body: configData.forwardCaptureBody,
              ctaText: configData.forwardCaptureCtaText,
              ctaUrl: configData.forwardCaptureCtaUrl,
              dismissMessage: configData.forwardCaptureDismissMessage,
            }}
            onComplete={handleCaptureComplete}
          />
        )}

        {state === 'config' && configData && (
          (() => {
            const intentType = configData.intentType || 'question_response';
            const Handler = INTENT_HANDLERS[intentType];
            if (!Handler) {
              return <UnknownIntentError intentType={intentType} />;
            }
            return <Handler config={configData} intentCode={intentCode} cid={contactId} onSelect={processResponse} />;
          })()
        )}

        {state === 'error' && <ErrorDisplay error={error} />}
      </div>
    </div>
  );
}

function ErrorDisplay({ error }) {
  const { status } = error || {};

  if (status === 404) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">This link is no longer active</h1>
        <p className="text-gray-600">The page you are looking for may have been removed or is unavailable.</p>
      </div>
    );
  }

  if (status === 410) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">This link has expired</h1>
        <p className="text-gray-600">This response link is no longer accepting submissions.</p>
      </div>
    );
  }

  if (status === 422) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">Invalid response</h1>
        <p className="text-gray-600">The response value in this link is not recognized.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <h1 className="text-xl font-semibold text-gray-900 mb-3">Something went wrong</h1>
      <p className="text-gray-600">Please try again later.</p>
    </div>
  );
}
